import React from 'react';

// Shared chrome for the empty-state marks: the 80x80 gradient-bordered tile that
// every one of them draws, plus the brand gradient (#7852F8 -> #84AEF6).
//
// The frame is part of the artwork (the outer <rect>), so these are passed to
// <NoDataV2 framed={false} /> - otherwise the component draws a second border
// around a mark that already has one.
//
// Gradient ids are per-instance (useId). A hard-coded id would collide if an
// icon rendered twice on a page, and every copy would inherit whichever <defs>
// mounted last.

export const GRADIENT_START = '#7852F8';
export const GRADIENT_END = '#84AEF6';

// The frame border is authored a shade off from the inner marks (#7853F8 vs
// #7852F8). Kept as exported from Figma rather than unified.
const FRAME_GRADIENT_START = '#7853F8';

// Marks supply their own <defs> as `defs`; `color` renders the icon flat and
// drops the gradients entirely.
const BrandIcon = ({ size = 80, color, children, defs, ...props }) => {
  const uid = React.useId();
  const frameGradient = `brand_frame_${uid}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 80 80"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      {...props}
    >
      <rect
        x="0.5"
        y="0.5"
        width="79"
        height="79"
        rx="11.5"
        stroke={color ?? `url(#${frameGradient})`}
      />
      {children}
      {!color && (
        <defs>
          <linearGradient
            id={frameGradient}
            x1="40"
            y1="0"
            x2="40"
            y2="80"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor={FRAME_GRADIENT_START} />
            <stop offset="1" stopColor={GRADIENT_END} />
          </linearGradient>
          {defs}
        </defs>
      )}
    </svg>
  );
};

// Each mark ramps the gradient over its own extent, so they cannot share a def.
// Coordinates are in the 80x80 user space.
export const BrandGradient = ({ id, x1, y1, x2, y2 }) => (
  <linearGradient
    id={id}
    x1={x1}
    y1={y1}
    x2={x2}
    y2={y2}
    gradientUnits="userSpaceOnUse"
  >
    <stop stopColor={GRADIENT_START} />
    <stop offset="1" stopColor={GRADIENT_END} />
  </linearGradient>
);

export default BrandIcon;
